/**
 * Modal reutilizavel de bloqueio de cliente.
 *
 * Bloquear != apagar. Cliente segue existindo, historico preservado,
 * parcelas em aberto continuam sendo cobradas. So impede novo emprestimo
 * (o cpf-lookup, o admin CreateLoan e o request-renewal recusam).
 *
 * Usado em ClientsPage, ClientDetailPage e LoansPage. O PipelinePage tem
 * um dialog inline proprio porque ja carrega dados da linha (nome + id
 * do cliente) — quando refatorarmos aquela pagina, migra pra este.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  useToast,
} from "@payflow/ui";
import { blockClient } from "../services/clients.service";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
  onBlocked?: () => void;
}

export function BlockClientDialog({ open, onOpenChange, clientId, clientName, onBlocked }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [motivo, setMotivo] = useState("");
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setMotivo("");
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await blockClient(clientId, motivo);
      toast({ variant: "success", title: t("pipeline.block.success") });
      handleClose();
      onBlocked?.();
    } catch (err) {
      toast({
        variant: "error",
        title: err instanceof Error ? err.message : t("pipeline.block.error"),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => (v ? onOpenChange(v) : handleClose())}
      dirty={motivo.trim() !== ""}
      onSave={async () => { await handleConfirm(); }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("pipeline.block.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            {t("pipeline.block.description")} <strong>{clientName}</strong>?
          </p>
          <p className="text-xs text-muted-foreground">
            {t("pipeline.block.consequence")}
          </p>
          <div>
            <label className="mb-1 block text-sm font-medium">
              {t("pipeline.block.reason_label")}
            </label>
            <textarea
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={t("pipeline.block.reason_placeholder")}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={loading}>
            {loading ? t("pipeline.block.blocking") : t("pipeline.block.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
